/**
 * Currency utilities for handling Naira amounts
 * All amounts are stored as integers in Naira (main currency unit)
 * No kobo conversion needed - everything works in Naira
 */

export const CURRENCY_CONSTANTS = {
  // Stream payment in Naira (main currency unit)
  STREAM_PAYMENT_NAIRA: 50,

  // Verified artist stream payment in Naira
  VERIFIED_STREAM_PAYMENT_NAIRA: 60,

  // Verification fee in Naira - Updated to ₦24,000
  VERIFICATION_FEE_NAIRA: 24000,

  // Minimum cashout amount in Naira
  MINIMUM_CASHOUT_NAIRA: 11500,

  // Low balance threshold for cashout eligibility
  LOW_BALANCE_THRESHOLD_NAIRA: 100,

  // Currency code
  CURRENCY_CODE: "NGN",

  // Currency symbol
  CURRENCY_SYMBOL: "₦",
} as const;

/**
 * Format amount in Naira for display
 * @param amountNaira - Amount in Naira (main currency unit)
 * @returns Formatted string like "₦1,234.56"
 */
export function formatNaira(amountNaira: number): string {
  if (typeof amountNaira !== "number" || isNaN(amountNaira)) {
    return "₦0";
  }

  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amountNaira);
}

/**
 * Convert Naira to kobo (for payment gateways that require kobo)
 * @param amountNaira - Amount in Naira
 * @returns Amount in kobo
 */
export function nairaToKobo(amountNaira: number): number {
  if (typeof amountNaira !== "number" || isNaN(amountNaira)) {
    return 0;
  }
  return Math.round(amountNaira * 100);
}

/**
 * Convert kobo to Naira
 * @param amountKobo - Amount in kobo
 * @returns Amount in Naira
 */
export function koboToNaira(amountKobo: number): number {
  if (typeof amountKobo !== "number" || isNaN(amountKobo)) {
    return 0;
  }
  return amountKobo / 100;
}

/**
 * Parse user input amount (in Naira) to number
 * @param input - User input string
 * @returns Amount in Naira or null if invalid
 */
export function parseNairaAmount(input: string): number | null {
  if (!input || typeof input !== "string") {
    return null;
  }

  // Remove currency symbols and whitespace
  const cleaned = input.replace(/[₦,\s]/g, "");
  const amount = parseFloat(cleaned);

  if (isNaN(amount) || amount < 0) {
    return null;
  }

  return Math.round(amount * 100) / 100; // Round to 2 decimal places
}

/**
 * Validate if amount is valid for Naira transactions
 * @param amountNaira - Amount in Naira
 * @returns True if valid
 */
export function isValidNairaAmount(amountNaira: number): boolean {
  return (
    typeof amountNaira === "number" &&
    !isNaN(amountNaira) &&
    amountNaira >= 0 &&
    amountNaira <= 10000000
  ); // Max 10M Naira
}

/**
 * Get minimum amount for operations in Naira
 */
export function getMinimumAmountNaira(): number {
  return 1000; // Minimum 1000 Naira for wallet top-up
}

/**
 * Get maximum amount for operations in Naira
 */
export function getMaximumAmountNaira(): number {
  return 1000000; // Maximum 1M Naira per transaction
}

/**
 * Convert a transaction amount to Naira based on its type.
 * Only verification_payment is stored in kobo; all other types are already in Naira.
 */
const KOBO_STORED_TYPES = new Set([
  "verification_payment",
  "gift_purchase",
  "event_ticket_purchase",
]);

export function getTransactionAmountInNaira(
  amount: number,
  type: string,
): number {
  if (KOBO_STORED_TYPES.has(type)) {
    return koboToNaira(amount);
  }
  return amount;
}

/**
 * Calculate fees for wallet top-up
 * @param baseAmount - Base amount in Naira
 * @returns Object with fee breakdown
 */
export function calculateTopupFees(baseAmount: number) {
  const vatAmount = Math.round(baseAmount * 0.075); // 7.5% VAT
  const processingFee = 1000; // ₦1,000 processing fee
  const totalCharged = baseAmount + vatAmount + processingFee;

  return {
    baseAmount,
    vatAmount,
    processingFee,
    totalCharged,
  };
}

/**
 * Calculate fees for gift payments
 * @param baseAmount - Base amount in Naira
 * @returns Object with fee breakdown
 */
export function calculateGiftFees(baseAmount: number) {
  const vatAmount = Math.round(baseAmount * 0.075); // 7.5% VAT
  const processingFee = Math.round(baseAmount * 0.05); // 5% processing fee for gifts
  const totalCharged = baseAmount + vatAmount + processingFee;

  return {
    baseAmount,
    vatAmount,
    processingFee,
    totalCharged,
  };
}

/**
 * Calculate fees for paid event ticket purchases.
 * @param baseAmount - Total ticket subtotal in Naira (price * quantity)
 * @returns Object with VAT (7.5%), processing fee (5%) and total
 */
export function calculateEventTicketFees(baseAmount: number) {
  const vatAmount = Math.round(baseAmount * 0.075); // 7.5% VAT
  const processingFee = Math.round(baseAmount * 0.05); // 5% processing fee
  const totalCharged = baseAmount + vatAmount + processingFee;

  return {
    baseAmount,
    vatAmount,
    processingFee,
    totalCharged,
  };
}
